<script setup lang="ts">
import {ref, set} from "vue"
import ListBrowserToolbar from "~components/admin/ListBrowserToolbar.vue"
import ListBrowserPagination from "~components/admin/ListBrowserPagination.vue"
import ExpandableJsonDetail from "~components/admin/ExpandableJsonDetail.vue"
import {useListBrowser} from "@/composables/useListBrowser"
import {formatDateTime, globParam, setQueryParam} from "@/common/utils/admin"

interface StripeWebhook {
  id: string
  date_created: string
  event_type: string
  stripe_customer_id: string | null
  stripe_user_email: string | null
  stripe_subscription_id: string | null
  stripe_environment: string | null
  stripe_amount: string | null
  stripe_event_created: string | null
  stripe_event_raw: Record<string, unknown> | null
  stripe_user_id: number | null
  stripe_user_name: string | null
  success: boolean
  _showDetails?: boolean
}

const eventTypeFilter = ref("")
const showUnprocessed = ref(false)

const {
  dateFrom, dateTo, currentPage, perPage, sortBy, sortDesc,
  totalRows, rows, lastPage, doSearch, onSortChanged,
  setDateRange, dateRangeActive,
} = useListBrowser({
  serviceUrl: "/service/objects/stripe_webhook",
  defaultSortColumn: "date_created",
  dateColumn: "date_created",
  buildFilterParams(params) {
    const typeFilter = eventTypeFilter.value.trim()
    if (typeFilter) {
      setQueryParam(params, "event_type_glob", globParam(typeFilter))
    }
    if (!showUnprocessed.value) {
      setQueryParam(params, "hide_unprocessed", true)
    }
  },
  readUrlParams(urlParams) {
    if (urlParams.has("event_type_glob")) {
      eventTypeFilter.value = urlParams.get("event_type_glob")!.replace(/\*$/, "")
    }
    if (urlParams.has("hide_unprocessed")) {
      showUnprocessed.value = urlParams.get("hide_unprocessed") === "0"
    }
  },
  buildUrlParams(urlParams) {
    // hide_unprocessed: 0 = show all, 1 (or absent) = hide unprocessed (default)
    if (showUnprocessed.value) {
      urlParams.set("hide_unprocessed", "0")
    } else {
      urlParams.delete("hide_unprocessed")  // absent means 1 (default)
    }
  },
})

const fields = [
  {key: "date_created", label: "Date", sortable: true, thStyle: {maxWidth: "10em"}},
  {key: "event_type", label: "Event Type", sortable: true},
  {key: "stripe_user_name", label: "User", sortable: false},
  {key: "stripe_user_email", label: "Email", sortable: false},
  {key: "stripe_subscription_id", label: "Subscription", sortable: false},
  {key: "stripe_environment", label: "Env", sortable: false, thStyle: {maxWidth: "4em"}},
  {key: "stripe_amount", label: "Amount", sortable: false, thStyle: {maxWidth: "7em"}},
  {key: "success", label: "Result", sortable: true, thStyle: {maxWidth: "5em"}},
]

const onRowClicked = (item: StripeWebhook) => set(item, "_showDetails", !item._showDetails)

const formatAmount = (v: string | null) => {
  if (!v) return "—"
  const cents = parseInt(v, 10)
  return isNaN(cents) ? "—" : "$" + (cents / 100).toFixed(2)
}

defineExpose({ eventTypeFilter, showUnprocessed, dateFrom, dateTo, currentPage, perPage, sortBy, sortDesc, rows, fields, doSearch, onRowClicked, setDateRange, dateRangeActive, formatDateTime })
</script>

<template>
  <div>
    <!-- Filter bar -->
    <b-row class="mb-3 align-items-end">
      <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">Event Type</label>
        <b-form-input
          v-model="eventTypeFilter"
          placeholder="e.g. customer.subscription"
          size="sm"
          @keydown.enter="doSearch()"
        />
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">From</label>
        <b-form-input v-model="dateFrom" type="date" size="sm" @keydown.enter="doSearch()" />
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">To</label>
        <b-form-input v-model="dateTo" type="date" size="sm" @keydown.enter="doSearch()" />
      </b-col>
      <b-col cols="12" sm="2" class="d-flex align-items-end">
        <b-form-checkbox v-model="showUnprocessed" class="mb-0" @change="doSearch()">
          Show unprocessed
        </b-form-checkbox>
      </b-col>
    </b-row>

    <list-browser-toolbar
      :date-range-active="dateRangeActive"
      @search="doSearch()"
      @set-date-range="setDateRange($event)"
    />

    <list-browser-pagination
      :current-page="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      :last-page="lastPage"
      @page-change="currentPage = $event; doSearch()"
      @per-page-change="perPage = $event; doSearch()"
    />

    <!-- Results -->
    <b-table
      :items="rows"
      :fields="fields"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :current-page="currentPage"
      :per-page="perPage"
      primary-key="id"
      striped
      hover
      responsive
      show-empty
      empty-text="Click Search to load results"
      @row-clicked="onRowClicked"
      @sort-changed="onSortChanged"
    >
      <template #cell(date_created)="data">
        <span class="date-cell">{{ formatDateTime(data.item.date_created) }}</span>
      </template>
      <template #cell(event_type)="data">
        <code>{{ data.item.event_type }}</code>
      </template>
      <template #cell(stripe_user_name)="data">
        <a
          v-if="data.item.stripe_user_id"
          :href="`/admin/users/edit/${data.item.stripe_user_id}`"
          target="_blank"
        >{{ data.item.stripe_user_name || '—' }}</a>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(stripe_user_email)="data">
        {{ data.item.stripe_user_email || '—' }}
      </template>
      <template #cell(stripe_subscription_id)="data">
        <a
          v-if="data.item.stripe_subscription_id"
          :href="`https://dashboard.stripe.com/subscriptions/${data.item.stripe_subscription_id}`"
          target="_blank"
        >{{ data.item.stripe_subscription_id }}</a>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(stripe_environment)="data">
        <b-badge :variant="data.item.stripe_environment === 'production' ? 'primary' : 'secondary'">
          {{ data.item.stripe_environment === 'production' ? 'Prod' : 'Dev' }}
        </b-badge>
      </template>
      <template #cell(stripe_amount)="data">
        {{ formatAmount(data.item.stripe_amount) }}
      </template>
      <template #cell(success)="data">
        <span v-if="data.item.result === 'Not processed'">—</span>
        <b-badge v-else :variant="data.item.success ? 'success' : 'danger'">
          {{ data.item.success ? 'Success' : 'Failed' }}
        </b-badge>
      </template>
      <template #row-details="data">
        <div class="p-3">
          <div class="row mb-2">
            <div class="col-sm-6">
              <dl class="row mb-0">
                <dt class="col-sm-5">Event ID</dt>
                <dd class="col-sm-7"><code>{{ data.item.event_id }}</code></dd>
                <dt class="col-sm-5">Date Created</dt>
                <dd class="col-sm-7">{{ formatDateTime(data.item.date_created) }}</dd>
                <dt class="col-sm-5">Result</dt>
                <dd
                  class="col-sm-7"
                  :style="{
                    backgroundColor: data.item.result === 'Not processed' ? '' : data.item.success ? '#e6ffe6' : '#ffe6e6'
                  }"
                >{{ data.item.result || '—' }}</dd>
              </dl>
            </div>
            <div class="col-sm-6">
              <dl class="row mb-0">
                <dt class="col-sm-5">Stripe Event</dt>
                <dd class="col-sm-7"><code>{{ data.item.stripe_event_created ? formatDateTime(data.item.stripe_event_created) : '—' }}</code></dd>
                <dt class="col-sm-5">Success</dt>
                <dd class="col-sm-7">
                  <span v-if="data.item.result === 'Not processed'">—</span>
                  <b-badge v-else :variant="data.item.success ? 'success' : 'danger'">
                    {{ data.item.success ? 'Yes' : 'No' }}
                  </b-badge>
                </dd>
                <dt class="col-sm-5">Record ID</dt>
                <dd class="col-sm-7"><code>{{ data.item.id }}</code></dd>
              </dl>
            </div>
          </div>
          <hr>
          <expandable-json-detail title="Stripe Event Raw Data" :source="data.item.stripe_event_raw" />
        </div>
      </template>
    </b-table>
  </div>
</template>
